/**
 * Page Driver Step Component
 *
 * Renders the Page Driver configuration step within the Page Setup Wizard.
 * Displays dimension picker dropdowns and select inputs for each KPI dimension,
 * allowing users to set page-level driver values that propagate to decklines.
 */

import { usePageSetupContext } from '@context/PageSetupContext';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { generateDriverDisplayOptions } from '@utils/kpiDriverHelpers';
import { useCallback, useMemo } from 'react';
import { KpiDimension } from 'types/PageSidePanel.types';

const baseClassName = 'page-driver-step';

const PageDriverStep = () => {
  const { pageDrivers, setPageDrivers, pageError } = usePageSetupContext();

  /** Convert raw KPIDrivers to DriverDisplayOptionMap for the shared component */
  const driverOptions = useMemo(() => generateDriverDisplayOptions(pageDrivers, null), [pageDrivers]);

  /** Handles dimension value changes from KPIDriversDisplay */
  const handleChange = useCallback(
    (_key: KpiDimension, updates: Partial<KPIDrivers>) => {
      setPageDrivers((prev) => ({ ...prev, ...updates }));
    },
    [setPageDrivers],
  );

  /** If there is an error, display it instead of the form */
  if (pageError) {
    return <Alert severity='error'>{pageError}</Alert>;
  }

  return (
    <Box className={baseClassName}>
      <KPIDriversDisplay
        className={`${baseClassName}__stack`}
        driverOptions={driverOptions}
        renderMode='dimensionSelect'
        hideSwitches
        excludeDimensions={[KpiDimension.REPORTING_CURRENCY]}
        onChange={handleChange}
      />
    </Box>
  );
};

export default PageDriverStep;
